import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useMutationState } from '@tanstack/react-query'

import { serverReachability } from '@/shared/lib/server-reachability'

export type ConnectionStatus = 'connected' | 'reconnecting' | 'offline'

type ConnectionContextValue = {
  status: ConnectionStatus
  pendingMutationsCount: number
}

type ConnectionInternalContextValue = {
  setConnectionStatus: (status: ConnectionStatus) => void
}

const ConnectionContext = createContext<ConnectionContextValue | null>(null)
const ConnectionInternalContext = createContext<ConnectionInternalContextValue | null>(null)

export function ConnectionProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [status, setStatus] = useState<ConnectionStatus>(
    serverReachability.getState() === 'reachable' ? 'connected' : 'offline',
  )

  const pendingMutations = useMutationState({
    filters: { status: 'pending' },
  })

  useEffect(() => {
    return serverReachability.subscribe((state) => {
      if (state === 'reachable') setStatus('connected')
      else setStatus('offline')
    })
  }, [])

  return (
    <ConnectionInternalContext.Provider value={{ setConnectionStatus: setStatus }}>
      <ConnectionContext.Provider
        value={{
          status,
          pendingMutationsCount: pendingMutations.length,
        }}
      >
        {children}
      </ConnectionContext.Provider>
    </ConnectionInternalContext.Provider>
  )
}

/** Read-only connection status — for UI components */
export function useConnection(): ConnectionContextValue {
  const context = useContext(ConnectionContext)
  if (!context) throw new Error('useConnection must be used within ConnectionProvider')
  return context
}

/** Internal setter — only for SSE provider to update connection status */
export function useConnectionSetter(): ConnectionInternalContextValue {
  const context = useContext(ConnectionInternalContext)
  if (!context) throw new Error('useConnectionSetter must be used within ConnectionProvider')
  return context
}
